/**
 * 「初日から稼げる」の稼ぎ（/money/play-earnings・2026-10-26 money/earn-build・企画書 docs/teams/money/earn-launch/plan.md §5 の絵）。
 * サーバー: GET /v1/money/earn（apps/server/src/modules/money/earnView.ts）。判断と形は playEarn.ts（node でテストする）。
 *
 * 見せる順（絵のとおり）:
 *   1. 今週の入れ物（KUMODeck の広告の予算 $500）と出る見込み・今の 1 プレイの額（山分けなら下がる）・今週の見込み（式つき）
 *   2. 数えた / 数えない / 減点（理由の細かいところは出さない = 試して守りを探れない）
 *   3. ゲームごと（確認中の札）と、保留中（30 日の確認）・送る準備中・送った額。受け取りは Amazon のギフトカード（アカウントのメール）
 *   4. 締めた週の一覧・下に「KUMODeck 自身の広告・枠の代金を払う」の 1 行と問い合わせ先
 * ギフトカードの国（GiftCountry・2026-10-26 earn-build-2）: カードは作り手の国の Amazon でしか使えない（founder「米国は amazon.com・カナダは amazon.ca 等」）=
 *   選ぶ欄を受け取りの所に置く。まだ数えが無い人（案内だけの画面）にも出す = 最初の週が締まる前に選んでもらう（未選択の行は送れない）
 * WHY 売上と残高（EarningsPage）と別の画面か: あちらは Stripe の出金の台帳（最初の公開で見せない = launchHidden.ts の payouts）。
 *   この稼ぎは週の締めで KUMODeck が払う別のお金の筋（Amazon のギフトカードを手で送る）= 混ぜると「出金できる額」と読める。
 * 見せない人（サーバーが 404）にはナビも出さない（MoneyShell の earnOn）。URL を直接開いたら「まだ使えません」。
 */
import { useState } from 'react';
import { api, type ApiError } from '../../api';
import { Empty, ErrorState, num, PageHeader, Spinner, StatTile } from '../../components/ui';
import { useResource } from '../../hooks';
import { errorText, intlLocale, useT } from '../../i18n';
import { money } from '../../money';
import { SUPPORT_EMAIL } from '../../guidePage';
import { isOff } from '../../selfAdsApi';
import { countryOptions, dayLabel, isEmpty, PLAY_EARN_API, PLAY_EARN_COUNTRY_API, poolPercent, ratePerPlay, weekRange, weekTone, type PlayEarnView } from '../../playEarn';

export function PlayEarnPage() {
  const { t } = useT();
  const r = useResource<PlayEarnView>(PLAY_EARN_API, 60_000);
  const loc = intlLocale();

  if (isOff(r.error)) {
    return (
      <>
        <PageHeader title={t('pe.title')} />
        <Empty title={t('pe.off.title')} icon="soon">
          <p>{t('pe.off.body')}</p>
        </Empty>
      </>
    );
  }
  if (r.loading && !r.data) return <Spinner />;
  if (r.error) return <ErrorState error={r.error} what={t('pe.title')} onRetry={r.reload} />;
  const v = r.data!;
  const usd = (c: number) => money(c, 'usd');
  const rate = ratePerPlay(v.rateCents, loc);
  const full = ratePerPlay(v.centsPerPlay, loc);
  const fullPlays = v.centsPerPlay > 0 ? Math.floor(v.poolCents / v.centsPerPlay) : 0;

  return (
    <>
      <PageHeader title={t('pe.title')} lead={t('pe.thisWeek', { range: weekRange(v.week.start, v.week.end, loc) })} />
      {!v.enabled && (
        <p className="alert alert-warn" role="status">
          {t('pe.paused')}
        </p>
      )}

      <section className="tiles pe-tiles" aria-label={t('pe.title')}>
        <StatTile
          label={t('pe.pool', {})}
          value={usd(v.poolCents)}
          hint={
            <>
              <span className="pe-bar" aria-hidden="true">
                <span className="pe-bar-fill" style={{ width: `${poolPercent(v)}%` }} />
              </span>
              {t('pe.poolUsed', { amount: usd(v.poolUsedCents) })}
            </>
          }
        />
        <StatTile
          label={t('pe.rate')}
          value={t('pe.rateValue', { rate })}
          tone={v.split ? 'warn' : undefined}
          hint={v.split ? t('pe.rateSplit', { pool: usd(v.poolCents), full }) : t('pe.rateFull', { plays: num(fullPlays), pool: usd(v.poolCents) })}
        />
        <StatTile
          label={t('pe.estimate')}
          value={usd(v.mine.estimateCents)}
          tone="accent"
          hint={
            <>
              {v.mine.underReview > 0
                ? t('pe.estimateMathReview', { counted: num(v.mine.countedPlays), deducted: num(v.mine.deducted), review: num(v.mine.underReview), net: num(v.mine.netPlays), rate })
                : v.mine.deducted > 0
                  ? t('pe.estimateMath', { counted: num(v.mine.countedPlays), deducted: num(v.mine.deducted), net: num(v.mine.netPlays), rate })
                  : t('pe.estimateMathPlain', { net: num(v.mine.netPlays), rate })}
              {v.mine.capped && <> · {t('pe.estimateCapped', { cap: usd(v.creatorWeeklyCapCents) })}</>}
            </>
          }
        />
      </section>

      <section className="tiles pe-tiles">
        <StatTile label={t('pe.counted')} value={num(v.mine.countedPlays)} hint={t('pe.countedHint')} />
        <StatTile label={t('pe.notCounted')} value={<span className="pe-muted">{num(v.mine.notCounted)}</span>} hint={t('pe.notCountedHint')} />
        <StatTile label={t('pe.deducted')} value={<span className={v.mine.deducted > 0 ? 'pe-minus' : 'pe-muted'}>{v.mine.deducted > 0 ? `−${num(v.mine.deducted)}` : '0'}</span>} hint={t('pe.deductedHint')} />
      </section>

      {isEmpty(v) ? (
        <>
          <Empty title={t('pe.empty.title')}>
            <p>{t('pe.empty.body')}</p>
          </Empty>
          <section className="card pe-money">
            <GiftCountry v={v} onSaved={r.reload} />
          </section>
        </>
      ) : (
        <div className="pe-split">
          <section className="card pe-games" aria-labelledby="pe-games">
            <h2 className="card-title" id="pe-games">
              {t('pe.byGame')}
            </h2>
            {v.games.length === 0 ? (
              <p className="muted small">{t('pe.noGames')}</p>
            ) : (
              <div className="table-wrap">
                <table className="table table-stack">
                  <thead>
                    <tr>
                      <th>{t('pe.col.game')}</th>
                      <th className="num">{t('pe.col.plays')}</th>
                      <th className="num">{t('pe.col.est')}</th>
                      <th className="num">{t('pe.col.status')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {v.games.map((g) => (
                      <tr key={g.projectId}>
                        <td>{g.name}</td>
                        <td className="num" data-label={t('pe.col.plays')}>{num(g.plays)}</td>
                        <td className="num" data-label={t('pe.col.est')}>{usd(g.estimateCents)}</td>
                        <td className="num" data-label={t('pe.col.status')}>
                          <span className={`pill ${g.status === 'ok' ? 'pill-ok' : 'pill-warn'}`}>{t(g.status === 'ok' ? 'pe.status.ok' : 'pe.status.review')}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {v.games.some((g) => g.status === 'review') && <p className="muted small">{t('pe.reviewNote')}</p>}
          </section>

          <section className="card pe-money" aria-label={t('pe.onHold', { days: v.holdDays })}>
            <div className="pe-money-row">
              <span className="tile-label">{t('pe.onHold', { days: v.holdDays })}</span>
              <strong className="pe-money-value">{usd(v.onHoldCents)}</strong>
              <span className="small muted">{t('pe.onHoldHint', { day: dayLabel(v.week.closesOn, loc) })}</span>
            </div>
            {v.readyCents > 0 && (
              <div className="pe-money-row">
                <span className="tile-label">{t('pe.ready')}</span>
                <strong className="pe-money-value">{usd(v.readyCents)}</strong>
                <span className="small muted">{t('pe.readyHint')}</span>
              </div>
            )}
            <div className="pe-money-row">
              <span className="tile-label">{t('pe.sent')}</span>
              <strong className="pe-money-value">{usd(v.sentCents)}</strong>
            </div>
            <p className="pe-pay small">{t('pe.payLine', { email: v.payout.email, days: v.holdDays })}</p>
            <GiftCountry v={v} onSaved={r.reload} />
          </section>
        </div>
      )}

      {v.weeks.length > 0 && (
        <section className="card" aria-labelledby="pe-weeks">
          <h2 className="card-title" id="pe-weeks">
            {t('pe.weeks')}
          </h2>
          <div className="table-wrap">
            <table className="table table-stack">
              <thead>
                <tr>
                  <th>{t('pe.col.week')}</th>
                  <th className="num">{t('pe.col.amount')}</th>
                  <th className="num">{t('pe.col.status')}</th>
                </tr>
              </thead>
              <tbody>
                {v.weeks.map((w) => (
                  <tr key={w.week}>
                    <td>{weekRange(w.week, addDays(w.week, 6), loc)}</td>
                    <td className="num" data-label={t('pe.col.amount')}>{usd(w.amountCents)}</td>
                    <td className="num" data-label={t('pe.col.status')}>
                      <span className={`pill ${weekTone(w.status) ? `pill-${weekTone(w.status)}` : ''}`}>
                        {w.status === 'held'
                          ? t('pe.week.held', { day: dayLabel(w.holdUntil, loc) })
                          : w.status === 'sent'
                            ? t('pe.week.sent', { day: dayLabel(w.sentOn ?? w.holdUntil, loc) })
                            : t(w.status === 'ready' ? 'pe.week.ready' : 'pe.week.voided')}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      <p className="small muted pe-foot">{t('pe.foot', { email: SUPPORT_EMAIL })}</p>
    </>
  );
}

/** 'YYYY-MM-DD' に n 日足す（UTC） */
const addDays = (day: string, n: number) => new Date(new Date(`${day}T00:00:00Z`).getTime() + n * 86_400_000).toISOString().slice(0, 10);

/**
 * ギフトカードを受け取る国（PUT /v1/money/earn/country・サーバー money/earnCountry.ts）。選んだらすぐ保存して画面を読み直す（保存のボタンは置かない =
 * 選ぶ欄 1 つで済む操作）。未選択 = 警告の 1 行に「Choose your Amazon country to receive gift cards」（指示書の文のまま）。選ぶ欄の頭は短く
 *   「Choose a country」（長い文は 390 幅の欄に入りきらず切れた = 2026-10-26 の撮影で確かめた）。
 * 選んだ後 = どの店で買うか（"amazon.ca"）とその店でだけ使えることを 1 行。失敗はその場に出す（画面ごと落とさない）
 */
function GiftCountry({ v, onSaved }: { v: PlayEarnView; onSaved: () => void }) {
  const { t } = useT();
  const loc = intlLocale();
  const [state, setState] = useState<{ busy: boolean; saved: boolean; error: string | null }>({ busy: false, saved: false, error: null });
  const options = countryOptions(v.payout.countries, loc);
  const pick = async (country: string | null) => {
    setState({ busy: true, saved: false, error: null });
    try {
      await api(PLAY_EARN_COUNTRY_API, { method: 'PUT', body: { country } });
      setState({ busy: false, saved: true, error: null });
      onSaved();
    } catch (e) {
      setState({ busy: false, saved: false, error: errorText(e as ApiError) });
    }
  };
  return (
    <div className="pe-country">
      <label className="field">
        <span>{t('pe.country.label')}</span>
        <select value={v.payout.country ?? ''} disabled={state.busy} onChange={(e) => void pick(e.target.value || null)}>
          {!v.payout.country && <option value="">{t('pe.country.placeholder')}</option>}
          {options.map((o) => (
            <option key={o.country} value={o.country}>
              {o.label}
            </option>
          ))}
        </select>
      </label>
      <p className="small muted" aria-live="polite">
        {state.busy ? t('pe.country.saving') : v.payout.store ? <>{t('pe.country.hint', { store: v.payout.store })}{state.saved && <> · {t('pe.country.saved')}</>}</> : null}
      </p>
      {!v.payout.country && !state.busy && (
        <p className="alert alert-warn small" role="status">
          {t('pe.country.choose')}
        </p>
      )}
      {state.error && (
        <p className="alert alert-error small" role="alert">
          {state.error}
        </p>
      )}
    </div>
  );
}
